import * as turf from '@turf/turf'
import { alertTypeCodeToLocalizedDisplayName, type CapSeverity, KnownAlertTypeCodeSchema } from '@xweather/protect-types'
import { round } from 'es-toolkit'
import { maxBy } from 'es-toolkit/compat'
import type { Geometry, Position } from 'geojson'
import i18next from 'i18next'
import { useMemo } from 'react'
import { create } from 'zustand'
import { combine } from 'zustand/middleware'
import type { IconName } from '../../components/Icon.tsx'
import { useAlerts } from '../../data/alerts/useAlerts.ts'
import { type AnyProtection, type LightningProtectionGroup, ProtectionType } from '../../data/protections/types.ts'
import { lightningRingToSeverityMap, protectionTypePriorityOrder } from '../../data/protections/utils.ts'
import { userListData } from '../../data/userLists.ts'
import type { UserPreferences } from '../../data/userProfile.ts'
import { dayjs } from '../../dayjs.ts'
import { mod } from '../../number.ts'

export const protectionIcons: Record<ProtectionType, IconName> = {
	[ProtectionType.Hail]: 'VHail',
	[ProtectionType.Lightning]: 'LightningBolt',
	[ProtectionType.Wind]: 'Wind',
	[ProtectionType.WeatherHazard]: 'Bullhorn',
	[ProtectionType.Measurement]: 'TriangleExclamation',
	[ProtectionType.WebRelay]: 'LightSwitch',
	[ProtectionType.Precipitation]: 'DropletPercent',
}

export const getHighestPriorityHailContext = <T extends { time: string; alertContextValue?: { HAIL_SEVERE_PROBABILITY?: string } }>(
	context: T[] | undefined,
): T | undefined => {
	if (!context?.length || !context[0]) return undefined
	const now = dayjs()
	return context.reduce((highest, current) => {
		const closestDiff = Math.abs(dayjs(highest?.time).diff(now))
		const currentDiff = Math.abs(dayjs(current.time).diff(now))
		if (closestDiff === currentDiff)
			return maxBy([highest, current], (ctx) => Number(ctx?.alertContextValue?.HAIL_SEVERE_PROBABILITY ?? 0)) ?? highest
		return currentDiff < closestDiff ? current : highest
	}, context[0])
}

export const calculateHailRange = (diff: number): string => {
	const lower = Math.floor(diff / 10) * 10
	const upper = diff % 10 === 0 ? lower : lower + 10
	return lower === upper ? `${lower + 1}-${upper + 10}` : `${lower + 1}-${upper}`
}

const sortProtection = (a: AnyProtection, b: AnyProtection) => {
	const aIndex = protectionTypePriorityOrder.indexOf(a.type)
	const bIndex = protectionTypePriorityOrder.indexOf(b.type)
	if (aIndex === bIndex && a.priority !== b.priority) return a.priority - b.priority
	return aIndex - bIndex
}

export const useSortedProtections = (protections: AnyProtection[]) => {
	const { alerts } = useAlerts()
	return useMemo(
		() => ({
			activeProtections: protections.filter(({ vrn }) => alerts[vrn]).toSorted(sortProtection),
			inactiveProtections: protections.filter(({ vrn }) => !alerts[vrn]).toSorted(sortProtection),
		}),
		[alerts, protections],
	)
}

export const bufferUnit: Record<UserPreferences['unitsDistance'], turf.Units> = {
	KILOMETRES: 'kilometers',
	MILES: 'miles',
	NAUTICAL_MILES: 'nauticalmiles',
}

export const createBufferedRing = (geometry: Geometry, radius: number, units: turf.Units) => {
	const buffered = turf.buffer(turf.feature(geometry), radius, { units, steps: geometry.type === 'Point' ? 100 : undefined })
	if (!buffered) throw new Error('Buffer operation failed: turf.buffer returned undefined')
	return buffered
}

export const useAlertRings = create(
	combine<AlertRingsState, { setAlertRings: (alertRings: AlertRingsState['alertRings']) => void }>(
		{ alertRings: { geometry: { type: 'Point', coordinates: [0, 0] }, rings: [] } },
		(set) => ({ setAlertRings: (alertRings) => set({ alertRings }) }),
	),
)

type AlertRingsState = {
	alertRings: { geometry: Geometry; rings: Ring[] }
}
type Ring = { radius: number; color: string; width: number; enabled?: boolean }

export const useSortedUserLists = () => {
	const { data: userLists } = userListData.use()
	const sortedUserLists = useMemo(
		() =>
			[...userLists].sort((a, b) => {
				if (a.isDefault) return -1
				if (b.isDefault) return 1
				return a.name.localeCompare(b.name)
			}),
		[userLists],
	)
	return sortedUserLists
}

export const getLightningRingLabel = (ringKey: keyof LightningProtectionGroup['rings'], isInfo2Enabled?: boolean) => {
	return `${i18next.t(($) => $.alerts.rings[lightningRingToSeverityMap[ringKey]])}${ringKey === 'info2' ? ' 2' : ringKey === 'info' && isInfo2Enabled ? ' 1' : ''}`
}

export const flattenGeometry = (geometry: Geometry): Position[] => {
	if (geometry.type === 'Point') return [geometry.coordinates]
	if (geometry.type === 'MultiPoint') return geometry.coordinates
	if (geometry.type === 'Polygon') return geometry.coordinates.flat()
	if (geometry.type === 'MultiPolygon') return geometry.coordinates.flat(2)
	if (geometry.type === 'LineString') return geometry.coordinates
	if (geometry.type === 'MultiLineString') return geometry.coordinates.flat()
	if (geometry.type === 'GeometryCollection') return geometry.geometries.flatMap(flattenGeometry)
	return []
}

export const fromMinutesToHours = (minutes: number) => minutes / 60
export const fromHoursToMinutes = (hours: number) => hours * 60

export const twoDecimals = (value: number) => round(value, 2)

export const degreesToCompass = (degrees: number) => {
	const degreesWrapped = mod(degrees, 360)
	return directions.find(([end]) => degreesWrapped < end)?.[1] ?? 'N'
}

const directions = [
	[22.5, 'N'],
	[67.5, 'NE'],
	[112.5, 'E'],
	[157.5, 'SE'],
	[202.5, 'S'],
	[247.5, 'SW'],
	[292.5, 'W'],
	[337.5, 'NW'],
	[360, 'N'],
] as const

export const containsAllCapSeverities = (capSeverities: CapSeverity[]) =>
	capSeverities.includes('EXTREME') &&
	capSeverities.includes('SEVERE') &&
	capSeverities.includes('MODERATE') &&
	capSeverities.includes('MINOR')

export const localizeAlertTypeName = (vtec: string, language: UserPreferences['language'], fallback: string) => {
	const parsed = KnownAlertTypeCodeSchema.safeParse(vtec)
	if (parsed.success) {
		const toLower = <T extends string>(str: T): Lowercase<T> => str.toLowerCase() as Lowercase<T>
		const localization = alertTypeCodeToLocalizedDisplayName(parsed.data)[toLower(language)]
		return localization ? localization : fallback
	}
	return fallback
}
